import React, { useEffect, useState } from 'react'
import { Table, Input, Form, Button, Skeleton, DatePicker, Popconfirm, Space } from 'antd'
import {
  EditTwoTone,
  DeleteTwoTone,
  SaveTwoTone,
  CloseCircleTwoTone,
} from '@ant-design/icons'
import { Checkbox } from 'antd'
import fetchApi from '../../../../../lib/utils/fetch-api'
import dayjs, { Dayjs } from 'dayjs'
import './index.css'

interface IProxyData {
  id?: 'new' | number | null
  number?: string
  start_date?: string
  end_date?: string
  active?: boolean
}

const TabProxy = ({ id }: { id: number }) => {
  const [form] = Form.useForm()
  const [data, setData] = useState<any>([])
  const [editingKey, setEditingKey] = useState<null | number | 'new'>(null)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    const getData = async () => {
      try {
        const result = await fetchApi(`/api/employee/${id}/proxy`)
        setData(result)
        setLoading(false)
      } catch (error) {
        console.error({ error })
        setLoading(false)
      }
    }
    getData()
  }, [id])

  const isEditing = (record: IProxyData) => record.id === editingKey

  const edit = (record: IProxyData) => {
    const formValues = {
      ...record,
      start_date: record.start_date ? dayjs(record.start_date) : null,
      end_date: record.end_date ? dayjs(record.end_date) : null
    }
    form.setFieldsValue(formValues)
    setEditingKey(record.id || null)
  }

  const cancel = (record: IProxyData) => {
    if (record?.id === 'new') {
      setData(data => data.filter(r => r.id !== record.id))
    }
    setEditingKey(null)
  }

  const save = async (itemId: number | 'new') => {
    try {
      const row = await form.validateFields()

      const dataToSave = {
        ...row,
        start_date: row.start_date ? row.start_date.format('YYYY-MM-DD') : null,
        end_date: row.end_date ? row.end_date.format('YYYY-MM-DD') : null,
        id: itemId !== 'new' ? itemId : undefined,
      }

      const result = await fetchApi(`/api/employee/${id}/proxy`, {
        method: 'POST',
        body: JSON.stringify(dataToSave)
      })
      const newId = result?.data?.id

      setData(data => data.map(item => (item.id === itemId) ? { ...item, ...dataToSave, id: newId } : item))
      setEditingKey(null)
    } catch (errInfo) {
      console.error('Validate Failed:', errInfo)
    }
  }

  const deleteRow = async (itemId?: null | number | 'new') => {
    try {
      if (!itemId) {
        return
      }
      await fetchApi(`/api/employee/${id}/proxy/${itemId}`, {
        method: 'DELETE',
      })
      setData(data => data.filter(item => (item.id !== itemId)))
    } catch (errInfo) {
      console.error('Validate Failed:', errInfo)
    }
  }

  const addRow = () => {
    const newRow: IProxyData = {
      id: 'new',
      number: undefined,
      start_date: undefined,
      end_date: undefined,
      active: true,
    }

    const nextData = [newRow, ...data]
    setData(nextData)
    form.setFieldsValue(newRow)
    setEditingKey('new')
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    return dayjs(dateString).format('DD.MM.YYYY')
  }

  const columns: any = [
    {
      title: 'Номер доверенности',
      dataIndex: 'number',
      editable: true,
      render: (text: string, record: IProxyData) => {
        const editing = isEditing(record)
        return editing ? (
          <Form.Item
            name='number'
            style={{ margin: 0 }}
            rules={[{ required: true, message: 'Укажите номер доверенности' }]}
            initialValue={text}
          >
            <Input />
          </Form.Item>
        ) : text
      }
    },
    {
      title: 'Дата начала',
      dataIndex: 'start_date',
      width: 150,
      editable: true,
      render: (text: string, record: IProxyData) => {
        const editing = isEditing(record)
        return editing ? (
          <Form.Item
            name='start_date'
            style={{ margin: 0 }}
            rules={[
              { required: true, message: 'Укажите дату начала' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const endDate = getFieldValue('end_date')
                  if (!value || !endDate || value.isBefore(endDate, 'day') || value.isSame(endDate, 'day')) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Дата начала не может быть позже даты окончания'))
                }
              })
            ]}
            initialValue={text ? dayjs(text) : null}
          >
            <DatePicker
              format='DD.MM.YYYY'
              disabledDate={(current) => {
                const endDate = form.getFieldValue('end_date')
                return endDate ? Boolean(current && current.isAfter(dayjs(endDate).endOf('day'))) : false
              }}
            />
          </Form.Item>
        ) : formatDate(text)
      }
    },
    {
      title: 'Дата окончания',
      dataIndex: 'end_date',
      width: 150,
      editable: true,
      render: (text: string, record: IProxyData) => {
        const editing = isEditing(record)
        return editing ? (
          <Form.Item
            name='end_date'
            style={{ margin: 0 }}
            rules={[
              { required: true, message: 'Укажите дату окончания' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue('start_date')
                  if (!value || !startDate || value.isAfter(startDate, 'day') || value.isSame(startDate, 'day')) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Дата окончания не может быть раньше даты начала'))
                }
              })
            ]}
            initialValue={text ? dayjs(text) : null}
          >
            <DatePicker
              format='DD.MM.YYYY'
              disabledDate={(current) => {
                const startDate = form.getFieldValue('start_date')
                return startDate ? Boolean(current && current.isBefore(dayjs(startDate).startOf('day'))) : false
              }}
            />
          </Form.Item>
        ) : formatDate(text)
      }
    },
    {
      title: 'Активна',
      dataIndex: 'active',
      width: 50,
      editable: true,
      render: (text: boolean, record: IProxyData) => {
        const editing = isEditing(record)
        return editing ? (
          <Space className='alignCenter'>
            <Form.Item
              name='active'
              style={{ margin: 0 }}
              valuePropName='checked'
              rules={[{ required: true, message: 'Укажите статус активности' }]}
            >
              <Checkbox />
            </Form.Item>
          </Space>
        ) : (
          <Space className='alignCenter'>
            <Checkbox checked={text} disabled />
          </Space>
        )
      }
    },
    {
      title: 'Действие',
      width: 50,
      dataIndex: 'operation',
      render: (_: any, record: IProxyData) => {
        const editable = isEditing(record)
        return editable ? (
          <Space className='alignCenter'>
            <Button onClick={() => save(record?.id || 0)} icon={<SaveTwoTone />} />
            <Popconfirm
              title='Отменить редактирование?'
              onConfirm={() => cancel(record)}
            >
              <Button icon={<CloseCircleTwoTone twoToneColor={'#e66262'}/>} />
            </Popconfirm>
          </Space>
        ) : (
          <Space className='alignCenter'>
            <Button
              onClick={() => edit(record)}
              icon={<EditTwoTone twoToneColor={editingKey ? '#bcbcbc' : undefined} />}
              disabled={Boolean(editingKey)}
            />
            {Boolean(record?.id) && (
              <Popconfirm
                title={<>
                  Вы уверены, что хотите удалить доверенность <b>№{record.number}?</b>
                </>}
                onConfirm={() => deleteRow(record.id)}
              >
                <Button
                  icon={<DeleteTwoTone twoToneColor={editingKey ? '#bcbcbc' : '#e66262'} />}
                  disabled={Boolean(editingKey)}
                />
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <>
      {loading ? (
        <Skeleton active />
      ) : (
        <Form form={form} component={false}>
          <Button
            disabled={Boolean(editingKey)}
            onClick={addRow}
            style={{ marginBottom: 10 }}
          >
            Добавить
          </Button>
          <Table
            bordered
            rowKey='id'
            dataSource={data}
            columns={columns}
            rowClassName='editable-row'
            pagination={false}
          />
        </Form>
      )}
    </>
  )
}

export default TabProxy